'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

export default function RegisterClient() {
  const router = useRouter();
  const { signUp } = useAuth();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const validatePassword = (pw: string) => pw.length >= 8;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!fullName.trim()) {
      setError('Veuillez entrer votre nom complet.');
      return;
    }
    if (!validatePassword(password)) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    setLoading(true);
    try {
      await signUp(email, password, { fullName });

      // Send branded activation email via Resend edge function
      // The edge function calls Supabase Admin API to generate the real confirmation link
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!;

      try {
        await fetch(`${supabaseUrl}/functions/v1/send-activation-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            email,
            fullName,
          }),
        });
      } catch {
        // Non-blocking: Supabase native email may still work as fallback
      }

      setSuccess(true);
    } catch (err: any) {
      const msg = err?.message || '';
      if (msg.includes('already registered') || msg.includes('already exists')) {
        setError('Un compte existe déjà avec cet email. Veuillez vous connecter.');
      } else if (msg.includes('invalid email')) {
        setError('Adresse email invalide.');
      } else if (msg.includes('weak password') || msg.includes('Password should be')) {
        setError('Mot de passe trop faible. Utilisez au moins 8 caractères.');
      } else {
        setError("Une erreur s'est produite. Veuillez réessayer.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center space-y-6">
          <div className="flex justify-center">
            <div className="w-20 h-20 rounded-full bg-positive/15 border border-positive/30 flex items-center justify-center">
              <Icon name="EnvelopeIcon" size={36} className="text-positive" />
            </div>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground mb-2">Vérifiez votre email</h1>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Un lien de confirmation a été envoyé à <span className="text-foreground font-medium">{email}</span>.<br />
              Cliquez sur le lien pour activer votre compte et commencer votre essai gratuit.
            </p>
          </div>
          <div className="p-4 rounded-xl bg-positive/8 border border-positive/20 text-left">
            <div className="flex items-start gap-3">
              <Icon name="GiftIcon" size={18} className="text-positive mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-sm font-semibold text-foreground">30 jours gratuits vous attendent</div>
                <div className="text-xs text-muted-foreground mt-0.5">Accès complet à toutes les fonctionnalités. Puis $29/mois.</div>
              </div>
            </div>
          </div>
          <Link
            href="/landing"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="ArrowLeftIcon" size={14} />
            Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left panel — form */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-16 xl:px-24">
        <div className="w-full max-w-md mx-auto">
          {/* Logo */}
          <Link href="/landing" className="inline-flex items-center gap-2.5 mb-10 group">
            <AppLogo size={32} />
            <span className="text-sm font-bold text-foreground group-hover:text-primary transition-colors">NHL Analytics Pro</span>
          </Link>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground tracking-tight mb-2">Créer un compte</h1>
            <p className="text-muted-foreground text-sm">
              30 jours gratuits · Aucune carte requise · Annulation à tout moment
            </p>
          </div>

          {/* Free trial badge */}
          <div className="flex items-center gap-3 p-3.5 rounded-xl bg-positive/8 border border-positive/25 mb-6">
            <div className="w-8 h-8 rounded-full bg-positive/20 flex items-center justify-center flex-shrink-0">
              <Icon name="GiftIcon" size={16} className="text-positive" />
            </div>
            <div>
              <div className="text-xs font-semibold text-foreground">🎉 Offre de bienvenue — 1 mois gratuit</div>
              <div className="text-2xs text-muted-foreground">Accès complet pendant 30 jours. Puis $29/mois.</div>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-3 p-3.5 rounded-xl bg-destructive/8 border border-destructive/25 mb-5">
              <Icon name="ExclamationCircleIcon" size={16} className="text-destructive mt-0.5 flex-shrink-0" />
              <p className="text-xs text-destructive leading-relaxed">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Full name */}
            <div>
              <label htmlFor="fullName" className="block text-xs font-medium text-foreground mb-1.5">
                Nom complet
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <Icon name="UserIcon" size={16} className="text-muted-foreground" />
                </div>
                <input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Jean Tremblay"
                  required
                  autoComplete="name"
                  className="w-full pl-9 pr-4 py-3 rounded-xl bg-card border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/20 transition-all"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-xs font-medium text-foreground mb-1.5">
                Adresse email
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <Icon name="EnvelopeIcon" size={16} className="text-muted-foreground" />
                </div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@exemple.com"
                  required
                  autoComplete="email"
                  className="w-full pl-9 pr-4 py-3 rounded-xl bg-card border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/20 transition-all"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-xs font-medium text-foreground mb-1.5">
                Mot de passe
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <Icon name="LockClosedIcon" size={16} className="text-muted-foreground" />
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 caractères"
                  required
                  autoComplete="new-password"
                  className="w-full pl-9 pr-10 py-3 rounded-xl bg-card border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/20 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                </button>
              </div>
              {password.length > 0 && (
                <div className="mt-1.5 flex items-center gap-1.5">
                  <div className={`h-1 flex-1 rounded-full transition-colors ${password.length >= 8 ? 'bg-positive' : 'bg-destructive/40'}`} />
                  <div className={`h-1 flex-1 rounded-full transition-colors ${password.length >= 12 ? 'bg-positive' : 'bg-border'}`} />
                  <div className={`h-1 flex-1 rounded-full transition-colors ${password.length >= 16 ? 'bg-positive' : 'bg-border'}`} />
                  <span className="text-2xs text-muted-foreground ml-1">
                    {password.length < 8 ? 'Trop court' : password.length < 12 ? 'Acceptable' : 'Fort'}
                  </span>
                </div>
              )}
            </div>

            {/* Confirm password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-xs font-medium text-foreground mb-1.5">
                Confirmer le mot de passe
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <Icon name="LockClosedIcon" size={16} className="text-muted-foreground" />
                </div>
                <input
                  id="confirmPassword"
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Répétez votre mot de passe"
                  required
                  autoComplete="new-password"
                  className={`w-full pl-9 pr-10 py-3 rounded-xl bg-card border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 transition-all ${
                    confirmPassword.length > 0
                      ? password === confirmPassword
                        ? 'border-positive/50 focus:border-positive/60 focus:ring-positive/20' :'border-destructive/50 focus:border-destructive/60 focus:ring-destructive/20' :'border-border focus:border-primary/60 focus:ring-primary/20'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  <Icon name={showConfirm ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                </button>
                {confirmPassword.length > 0 && (
                  <div className="absolute right-9 top-1/2 -translate-y-1/2">
                    <Icon
                      name={password === confirmPassword ? 'CheckCircleIcon' : 'XCircleIcon'}
                      size={16}
                      className={password === confirmPassword ? 'text-positive' : 'text-destructive'}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Terms */}
            <p className="text-2xs text-muted-foreground leading-relaxed">
              En créant un compte, vous acceptez nos{' '}
              <span className="text-primary cursor-pointer hover:underline">Conditions d'utilisation</span>{' '}
              et notre{' '}
              <span className="text-primary cursor-pointer hover:underline">Politique de confidentialité</span>.
            </p>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-all hover:shadow-lg hover:shadow-primary/25 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Création du compte…
                </>
              ) : (
                <>
                  <Icon name="RocketLaunchIcon" size={16} />
                  Créer mon compte — Gratuit
                </>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-border" />
            <span className="text-2xs text-muted-foreground">Déjà inscrit ?</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Login link */}
          <Link
            href="/landing"
            className="block w-full py-3 rounded-xl border border-border text-sm font-medium text-center text-muted-foreground hover:text-foreground hover:border-primary/40 transition-all"
          >
            Se connecter
          </Link>
        </div>
      </div>

      {/* Right panel — visual (hidden on mobile) */}
      <div className="hidden lg:flex flex-1 flex-col justify-center items-center bg-gradient-to-br from-primary/10 via-background to-purple-900/15 border-l border-border p-12 relative overflow-hidden">
        {/* Background blobs */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-primary/8 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-20 left-10 w-56 h-56 bg-purple-500/8 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-sm text-center space-y-8">
          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { value: '99.2%', label: 'Uptime données', color: 'text-positive', bg: 'bg-positive/8 border-positive/20' },
              { value: '5,000+', label: 'Analystes actifs', color: 'text-primary', bg: 'bg-primary/8 border-primary/20' },
              { value: '30s', label: 'Délai données NHL', color: 'text-foreground', bg: 'bg-muted/30 border-border' },
              { value: '12,400', label: 'Projections/jour', color: 'text-foreground', bg: 'bg-muted/30 border-border' },
            ].map((s) => (
              <div key={s.label} className={`p-4 rounded-xl border ${s.bg}`}>
                <div className={`text-2xl font-bold font-mono-data ${s.color}`}>{s.value}</div>
                <div className="text-2xs text-muted-foreground mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Features list */}
          <div className="space-y-3 text-left">
            {[
              { icon: 'BoltIcon', text: 'Données NHL live en 30 secondes', color: 'text-positive' },
              { icon: 'SparklesIcon', text: '6 agents IA autonomes 24/7', color: 'text-primary' },
              { icon: 'ChartBarIcon', text: 'Analytics institutionnels', color: 'text-purple-400' },
              { icon: 'ShieldCheckIcon', text: 'SLA 99.9% garanti', color: 'text-amber-400' },
            ].map((f) => (
              <div key={f.text} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-card border border-border flex items-center justify-center flex-shrink-0">
                  <Icon name={f.icon as any} size={16} className={f.color} />
                </div>
                <span className="text-sm text-muted-foreground">{f.text}</span>
              </div>
            ))}
          </div>

          {/* Trust */}
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <Icon name="LockClosedIcon" size={12} className="text-positive" />
            <span>Données chiffrées · Aucune carte requise</span>
          </div>
        </div>
      </div>
    </div>
  );
}
